// Medical Store Receipt & PDF Generator Utility
// Generates authentic Pharmacy / Medical Store Invoices & Receipts for Backups and Individual Customer Bills

export const DEFAULT_STORE_INFO = {
  name: 'SAI PHARMACY & MEDICAL',
  subtitle: 'Retail Chemist & Druggist',
  address: 'Shop No. 1, Main Market, City',
  phone: '9876543210',
  dlNo: '20B/21B-MH-12345',
  gstin: '27AAAAA0000A1Z5'
};

// Returns configured store info from localStorage if available
export const getStoreInfo = () => {
  try {
    const saved = localStorage.getItem('storeDetails');
    if (saved) {
      return { ...DEFAULT_STORE_INFO, ...JSON.parse(saved) };
    }
  } catch (e) {
    // ignore
  }
  return DEFAULT_STORE_INFO;
};

// Generates CSS styles for an authentic pharmacy bill / receipt
const getReceiptStyles = () => `
  * { box-sizing: border-box; margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; }
  body { background: #f8fafc; color: #0f172a; padding: 20px; font-size: 12px; }
  .no-print-toolbar {
    max-width: 820px;
    margin: 0 auto 16px auto;
    background: #0f172a;
    color: #fff;
    padding: 10px 20px;
    border-radius: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 4px 12px rgba(15,23,42,0.15);
  }
  .toolbar-title { font-size: 13px; font-weight: 800; display: flex; items-center; gap: 8px; }
  .toolbar-btn-group { display: flex; gap: 10px; }
  .btn-print {
    background: #10b981;
    color: #fff;
    border: none;
    padding: 8px 18px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.2s;
  }
  .btn-print:hover { background: #059669; }
  .btn-close {
    background: #334155;
    color: #fff;
    border: none;
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
  }
  .btn-close:hover { background: #475569; }
  .receipt-page {
    max-width: 820px;
    margin: 0 auto 24px auto;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    padding: 24px 28px;
    box-shadow: 0 4px 14px rgba(0,0,0,0.05);
    page-break-after: always;
  }
  .receipt-page:last-child {
    page-break-after: auto;
    margin-bottom: 0;
  }
  .r-head {
    text-align: center;
    border-bottom: 2px solid #0f172a;
    padding-bottom: 12px;
    margin-bottom: 12px;
  }
  .r-store {
    font-size: 22px;
    font-weight: 900;
    letter-spacing: 0.5px;
    color: #0f172a;
    text-transform: uppercase;
  }
  .r-sub {
    font-size: 11px;
    color: #475569;
    font-weight: 600;
    margin-top: 2px;
  }
  .r-license {
    font-size: 10px;
    color: #64748b;
    margin-top: 4px;
    font-weight: 600;
  }
  .r-title-badge {
    display: inline-block;
    margin-top: 8px;
    font-size: 12px;
    font-weight: 900;
    letter-spacing: 2px;
    text-transform: uppercase;
    background: #f1f5f9;
    border: 1px solid #94a3b8;
    padding: 2px 14px;
    border-radius: 4px;
    color: #0f172a;
  }
  .r-info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px dashed #cbd5e1;
    margin-bottom: 14px;
    font-size: 11.5px;
  }
  .info-row { display: flex; margin-bottom: 3px; }
  .info-lbl { width: 110px; color: #64748b; font-weight: 700; }
  .info-val { font-weight: 800; color: #0f172a; text-transform: uppercase; }
  .r-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 14px;
    font-size: 11.5px;
  }
  .r-table th {
    background: #f8fafc;
    border-top: 1.5px solid #0f172a;
    border-bottom: 1.5px solid #0f172a;
    padding: 7px 6px;
    text-align: left;
    font-size: 9.5px;
    font-weight: 900;
    text-transform: uppercase;
    color: #334155;
    letter-spacing: 0.5px;
  }
  .r-table td {
    padding: 6px 6px;
    border-bottom: 1px solid #f1f5f9;
    color: #1e293b;
    vertical-align: middle;
  }
  .r-table tr:last-child td { border-bottom: 1.5px solid #0f172a; }
  .c { text-align: center; }
  .r { text-align: right; }
  .med-name { font-weight: 800; color: #0f172a; text-transform: uppercase; }
  .r-summary-wrap {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-top: 8px;
    font-size: 11.5px;
  }
  .r-notes-side {
    width: 50%;
    color: #64748b;
    font-size: 10.5px;
    line-height: 1.5;
  }
  .r-calc-side {
    width: 45%;
  }
  .calc-line {
    display: flex;
    justify-content: space-between;
    padding: 3px 0;
  }
  .calc-lbl { color: #475569; font-weight: 700; }
  .calc-val { font-weight: 800; color: #0f172a; }
  .net-line {
    display: flex;
    justify-content: space-between;
    border-top: 2px solid #0f172a;
    border-bottom: 2px solid #0f172a;
    padding: 6px 0;
    margin-top: 6px;
    font-size: 15px;
    font-weight: 900;
    color: #0f172a;
  }
  .r-footer-sign {
    margin-top: 22px;
    padding-top: 12px;
    border-top: 1px dashed #cbd5e1;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    font-size: 10.5px;
    color: #64748b;
  }
  .sign-area {
    text-align: center;
    width: 170px;
    border-top: 1px solid #94a3b8;
    padding-top: 4px;
    font-weight: 700;
    color: #334155;
  }
  .pill-badge {
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 9px;
    font-weight: 800;
    text-transform: uppercase;
    display: inline-block;
  }
  .pill-cash { background: #ecfdf5; color: #059669; border: 1px solid #a7f3d0; }
  .pill-online { background: #eff6ff; color: #2563eb; border: 1px solid #bfdbfe; }
  .pill-credit { background: #fff1f2; color: #e11d48; border: 1px solid #fecdd3; }
  
  @media print {
    body { background: #fff !important; padding: 0 !important; }
    .no-print-toolbar { display: none !important; }
    .receipt-page {
      border: 1px solid #94a3b8 !important;
      box-shadow: none !important;
      padding: 16px 20px !important;
      margin: 0 auto 0 auto !important;
      page-break-after: always !important;
    }
    .receipt-page:last-child {
      page-break-after: auto !important;
    }
  }
`;

// Helper: Formats single bill into receipt HTML markup
export const renderSingleBillReceiptMarkup = (bill, index = 1, store = DEFAULT_STORE_INFO, personHeaderNote = null) => {
  const items = bill.items || [];
  const grossAmount = items.reduce((sum, it) => sum + (parseFloat(it.amount || ((parseFloat(it.qty) || 1) * (parseFloat(it.rate) || 0))) || 0), 0);
  const netAmount = parseFloat(bill.netAmount || bill.grossAmount || grossAmount || 0);
  const discountVal = parseFloat(bill.discount || 0);
  const gstVal = parseFloat(bill.gstAmount || 0);

  const saleType = (bill.saleType || 'CASH').toUpperCase();
  const pillClass = saleType === 'CASH' ? 'pill-cash' : saleType === 'ONLINE' ? 'pill-online' : 'pill-credit';

  return `
    <div class="receipt-page">
      <div class="r-head">
        <div class="r-store">${store.name}</div>
        <div class="r-sub">${store.subtitle} • ${store.address} • Ph: ${store.phone}</div>
        <div class="r-license">D.L. No: ${store.dlNo} | GSTIN: ${store.gstin}</div>
        <div class="r-title-badge">TAX INVOICE / CASH MEMO</div>
      </div>

      ${personHeaderNote ? `
        <div style="background:#f8fafc;border:1px solid #e2e8f0;padding:6px 12px;border-radius:6px;margin-bottom:10px;font-size:11px;font-weight:700;color:#0284c7;">
          ${personHeaderNote}
        </div>
      ` : ''}

      <div class="r-info-grid">
        <div>
          <div class="info-row"><span class="info-lbl">Customer Name:</span><span class="info-val">${bill.custName || 'Walk-in Customer'}</span></div>
          ${bill.custMob ? `<div class="info-row"><span class="info-lbl">Contact No:</span><span class="info-val">📞 ${bill.custMob}</span></div>` : ''}
          ${bill.doctorName ? `<div class="info-row"><span class="info-lbl">Doctor:</span><span class="info-val">Dr. ${bill.doctorName}</span></div>` : ''}
          <div class="info-row"><span class="info-lbl">Payment Mode:</span><span class="pill-badge ${pillClass}">${saleType}</span></div>
        </div>
        <div>
          <div class="info-row"><span class="info-lbl">Invoice / Bill #:</span><span class="info-val" style="color:#0284c7">#${bill.invoiceNo || bill.vchNo || ('BILL-' + (index < 10 ? '0' + index : index))}</span></div>
          <div class="info-row"><span class="info-lbl">Date:</span><span class="info-val">${bill.date || new Date().toISOString().split('T')[0]}</span></div>
          <div class="info-row"><span class="info-lbl">Time:</span><span class="info-val">${bill.time || '-'}</span></div>
          <div class="info-row"><span class="info-lbl">Ref / Status:</span><span class="info-val" style="color:#059669">Verified Backup</span></div>
        </div>
      </div>

      <table class="r-table">
        <thead>
          <tr>
            <th style="width:5%">#</th>
            <th style="width:40%">Medicine / Item Description</th>
            <th style="width:13%">Batch</th>
            <th style="width:10%">Exp</th>
            <th style="width:8%" class="c">Qty</th>
            <th style="width:12%" class="r">Rate</th>
            <th style="width:12%" class="r">Amount (₹)</th>
          </tr>
        </thead>
        <tbody>
          ${items.length === 0 ? `
            <tr><td colspan="7" class="c" style="padding:16px;color:#94a3b8">No items recorded in bill.</td></tr>
          ` : items.map((it, i) => `
            <tr>
              <td class="c">${i + 1}</td>
              <td><span class="med-name">${it.productName || '-'}</span></td>
              <td>${it.batch || '-'}</td>
              <td>${it.exp || '-'}</td>
              <td class="c" style="font-weight:800;color:#0284c7">${it.qty || 1}</td>
              <td class="r">₹${parseFloat(it.rate || 0).toFixed(2)}</td>
              <td class="r" style="font-weight:900">₹${parseFloat(it.amount || ((parseFloat(it.qty) || 1) * (parseFloat(it.rate) || 0))).toFixed(2)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <div class="r-summary-wrap">
        <div class="r-notes-side">
          <p><strong>Terms & Conditions:</strong></p>
          <p>1. Goods once sold cannot be returned without original cash memo.</p>
          <p>2. Prescribed medicines must be taken strictly as per doctor advice.</p>
          <p style="margin-top:4px;color:#059669;font-weight:700">★ Thank you for your visit! Wishing you a speedy recovery.</p>
        </div>
        <div class="r-calc-side">
          <div class="calc-line">
            <span class="calc-lbl">Gross / Subtotal:</span>
            <span class="calc-val">₹${grossAmount.toFixed(2)}</span>
          </div>
          ${discountVal > 0 ? `
            <div class="calc-line">
              <span class="calc-lbl">Discount:</span>
              <span class="calc-val" style="color:#e11d48">- ₹${discountVal.toFixed(2)}</span>
            </div>
          ` : ''}
          ${gstVal > 0 ? `
            <div class="calc-line">
              <span class="calc-lbl">GST Amount:</span>
              <span class="calc-val">₹${gstVal.toFixed(2)}</span>
            </div>
          ` : ''}
          <div class="net-line">
            <span>NET AMOUNT:</span>
            <span>₹${netAmount.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div class="r-footer-sign">
        <div>
          <span>Items: <strong>${items.length}</strong> | Total Qty: <strong>${items.reduce((s, it) => s + (parseInt(it.qty) || 1), 0)}</strong></span><br/>
          <span style="font-size:9.5px;color:#94a3b8">Computerized Pharmacy Backup Invoice</span>
        </div>
        <div class="sign-area">
          Authorized Pharmacist
        </div>
      </div>
    </div>
  `;
};

// Generates complete Document for a Single Bill
export const generateSingleBillReceiptHtml = (bill, store = getStoreInfo()) => {
  const invNo = bill.invoiceNo || bill.vchNo || 'BILL';
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Receipt - ${bill.custName || 'Customer'} - ${invNo}</title>
  <style>${getReceiptStyles()}</style>
</head>
<body>
  <div class="no-print-toolbar">
    <div class="toolbar-title">
      <span>🧾</span>
      <span>Medical Bill Receipt (#${invNo}) - ${bill.custName || 'Customer'}</span>
    </div>
    <div class="toolbar-btn-group">
      <button class="btn-print" onclick="window.print()">🖨️ Print / Save as PDF</button>
      <button class="btn-close" onclick="window.close()">✕ Close</button>
    </div>
  </div>
  ${renderSingleBillReceiptMarkup(bill, 1, store)}
  <script>
    window.onload = function() {
      // Auto-trigger print dialog for effortless 1-click Save as PDF
      setTimeout(function() { window.print(); }, 350);
    };
  </script>
</body>
</html>`;
};

// Generates Multi-Bill Separate Receipts Document for a Specific Person
export const generatePersonBackupReceiptHtml = (personName, personSales = [], creditAccount = null, store = getStoreInfo()) => {
  const totalSpent = personSales.reduce((sum, s) => sum + (parseFloat(s.netAmount || s.grossAmount || 0) || 0), 0);
  const dueAmount = creditAccount ? parseFloat(creditAccount.dueAmount || 0) : 0;
  const phone = personSales[0]?.custMob || creditAccount?.phone || '-';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Person Backup Bills - ${personName}</title>
  <style>${getReceiptStyles()}</style>
</head>
<body>
  <div class="no-print-toolbar">
    <div class="toolbar-title">
      <span>👤</span>
      <span>Specific Person Backup: <strong>${personName}</strong> (${personSales.length} Separate Bills)</span>
    </div>
    <div class="toolbar-btn-group">
      <button class="btn-print" onclick="window.print()">🖨️ Print / Save All as PDF</button>
      <button class="btn-close" onclick="window.close()">✕ Close</button>
    </div>
  </div>

  <!-- Person Summary Header Sheet -->
  <div class="receipt-page" style="page-break-after: ${personSales.length > 0 ? 'always' : 'auto'};">
    <div class="r-head">
      <div class="r-store">${store.name}</div>
      <div class="r-sub">${store.subtitle} • ${store.address} • Ph: ${store.phone}</div>
      <div class="r-license">D.L. No: ${store.dlNo} | GSTIN: ${store.gstin}</div>
      <div class="r-title-badge">CUSTOMER BACKUP REGISTER & BILL SUMMARY</div>
    </div>

    <div style="background: linear-gradient(135deg, #f8fafc, #edf2f7); border: 1.5px solid #cbd5e1; border-radius: 10px; padding: 16px; margin: 14px 0;">
      <div style="display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:10px;">
        <div>
          <span style="font-size:10px; font-weight:800; color:#64748b; text-transform:uppercase;">Account / Customer</span>
          <h2 style="font-size:20px; font-weight:900; color:#0f172a; text-transform:uppercase; margin-top:2px;">${personName}</h2>
          <p style="font-size:12px; color:#475569; font-weight:600; margin-top:2px;">📞 Phone: ${phone}</p>
        </div>
        <div style="display:flex; gap:16px;">
          <div style="background:#fff; border:1px solid #cbd5e1; padding:10px 16px; border-radius:8px; text-align:center;">
            <div style="font-size:10px; font-weight:800; color:#64748b; text-transform:uppercase;">Total Invoices</div>
            <div style="font-size:18px; font-weight:900; color:#0284c7;">${personSales.length} Bills</div>
          </div>
          <div style="background:#fff; border:1px solid #cbd5e1; padding:10px 16px; border-radius:8px; text-align:center;">
            <div style="font-size:10px; font-weight:800; color:#64748b; text-transform:uppercase;">Total Purchases</div>
            <div style="font-size:18px; font-weight:900; color:#059669;">₹${totalSpent.toFixed(2)}</div>
          </div>
          ${creditAccount ? `
            <div style="background:#fff; border:1px solid #fecdd3; padding:10px 16px; border-radius:8px; text-align:center;">
              <div style="font-size:10px; font-weight:800; color:#e11d48; text-transform:uppercase;">Udhari / Due Balance</div>
              <div style="font-size:18px; font-weight:900; color:#e11d48;">₹${dueAmount.toFixed(2)}</div>
            </div>
          ` : ''}
        </div>
      </div>
    </div>

    <div style="margin: 16px 0;">
      <h3 style="font-size:13px; font-weight:900; color:#0f172a; text-transform:uppercase; margin-bottom:8px; border-bottom:1.5px solid #0f172a; padding-bottom:4px;">
        Index of Bills Included in this Person's Backup (${personSales.length} Records)
      </h3>
      <table class="r-table">
        <thead>
          <tr>
            <th style="width:5%">#</th>
            <th style="width:20%">Invoice #</th>
            <th style="width:20%">Date & Time</th>
            <th style="width:15%">Payment</th>
            <th style="width:15%" class="c">Items</th>
            <th style="width:25%" class="r">Total Amount</th>
          </tr>
        </thead>
        <tbody>
          ${personSales.length === 0 ? `
            <tr><td colspan="6" class="c" style="padding:16px;color:#94a3b8">No bills recorded for this person.</td></tr>
          ` : personSales.map((s, idx) => `
            <tr>
              <td class="c">${idx + 1}</td>
              <td style="font-weight:800;color:#0284c7">#${s.invoiceNo || s.vchNo || ('BILL-' + (idx + 1))}</td>
              <td>${s.date} ${s.time ? '• ' + s.time : ''}</td>
              <td><span class="pill-badge ${s.saleType === 'CASH' ? 'pill-cash' : s.saleType === 'ONLINE' ? 'pill-online' : 'pill-credit'}">${s.saleType || 'Cash'}</span></td>
              <td class="c">${(s.items || []).length} items</td>
              <td class="r" style="font-weight:900">₹${parseFloat(s.netAmount || s.grossAmount || 0).toFixed(2)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>

    <div class="r-footer-sign">
      <div>
        <span>Customer Backup Statement generated on: <strong>${new Date().toLocaleDateString('en-IN')} ${new Date().toLocaleTimeString('en-IN')}</strong></span>
      </div>
      <div class="sign-area">Authorized Pharmacist</div>
    </div>
  </div>

  <!-- SEPARATE BILLS FOR THIS PERSON (Each bill prints on its own clean receipt sheet) -->
  ${personSales.map((s, idx) => 
    renderSingleBillReceiptMarkup(s, idx + 1, store, `Bill ${idx + 1} of ${personSales.length} for ${personName}`)
  ).join('')}

  <script>
    window.onload = function() {
      setTimeout(function() { window.print(); }, 350);
    };
  </script>
</body>
</html>`;
};

// Generates Full Backup Register in Authentic Pharmacy Receipt Format
export const generateFullBackupReceiptHtml = (backupDoc, store = getStoreInfo()) => {
  const data = backupDoc.data || backupDoc;
  const sales = data.sales || [];
  const purchases = data.purchases || [];
  const credit = data.credit || [];
  const stockists = data.stockist || [];

  const totalSalesRevenue = sales.reduce((sum, s) => sum + (parseFloat(s.netAmount || s.grossAmount || 0) || 0), 0);
  const totalPurchaseValue = purchases.reduce((sum, p) => sum + (parseFloat(p.netAmount || p.grossAmount || p.totalAmount || 0) || 0), 0);
  const totalCreditDue = credit.reduce((sum, c) => sum + (parseFloat(c.dueAmount || 0) || 0), 0);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Medical Store Backup Register - ${backupDoc.date || ''}</title>
  <style>${getReceiptStyles()}</style>
</head>
<body>
  <div class="no-print-toolbar">
    <div class="toolbar-title">
      <span>🗄️</span>
      <span>Medical Backup Bill Register • <strong>${backupDoc.name || 'System Backup'}</strong> (${sales.length} Sales Bills)</span>
    </div>
    <div class="toolbar-btn-group">
      <button class="btn-print" onclick="window.print()">🖨️ Print / Save as PDF</button>
      <button class="btn-close" onclick="window.close()">✕ Close</button>
    </div>
  </div>

  <!-- Overview Summary Page -->
  <div class="receipt-page">
    <div class="r-head">
      <div class="r-store">${store.name}</div>
      <div class="r-sub">${store.subtitle} • ${store.address} • Ph: ${store.phone}</div>
      <div class="r-license">D.L. No: ${store.dlNo} | GSTIN: ${store.gstin}</div>
      <div class="r-title-badge">MASTER SYSTEM BACKUP & INVOICE REGISTER</div>
    </div>

    <div style="background:#f8fafc; border:1px solid #cbd5e1; border-radius:8px; padding:14px; margin:12px 0;">
      <div style="display:grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap:12px;">
        <div>
          <span style="font-size:10px; font-weight:800; color:#64748b; text-transform:uppercase;">Snapshot Date & Time</span>
          <div style="font-size:14px; font-weight:800; color:#0f172a; margin-top:2px;">${backupDoc.date || ''} ${backupDoc.time || ''}</div>
          <p style="font-size:11px; color:#64748b; margin-top:2px;">Terminal: ${backupDoc.deviceInfo || 'PC Terminal'}</p>
        </div>
        <div style="background:#fff; border:1px solid #cbd5e1; padding:8px 12px; border-radius:6px;">
          <span style="font-size:10px; font-weight:800; color:#059669; text-transform:uppercase;">Sales Bills (${sales.length})</span>
          <div style="font-size:16px; font-weight:900; color:#059669;">₹${totalSalesRevenue.toFixed(2)}</div>
        </div>
        <div style="background:#fff; border:1px solid #cbd5e1; padding:8px 12px; border-radius:6px;">
          <span style="font-size:10px; font-weight:800; color:#7c3aed; text-transform:uppercase;">Purchases (${purchases.length})</span>
          <div style="font-size:16px; font-weight:900; color:#7c3aed;">₹${totalPurchaseValue.toFixed(2)}</div>
        </div>
        <div style="background:#fff; border:1px solid #cbd5e1; padding:8px 12px; border-radius:6px;">
          <span style="font-size:10px; font-weight:800; color:#e11d48; text-transform:uppercase;">Credit Due (${credit.length})</span>
          <div style="font-size:16px; font-weight:900; color:#e11d48;">₹${totalCreditDue.toFixed(2)}</div>
        </div>
        <div style="background:#fff; border:1px solid #cbd5e1; padding:8px 12px; border-radius:6px;">
          <span style="font-size:10px; font-weight:800; color:#0284c7; text-transform:uppercase;">Stockists (${stockists.length})</span>
          <div style="font-size:16px; font-weight:900; color:#0284c7;">${stockists.length} Parties</div>
        </div>
      </div>
    </div>

    <div style="margin: 16px 0;">
      <h3 style="font-size:12px; font-weight:900; color:#0f172a; text-transform:uppercase; margin-bottom:8px; border-bottom:1.5px solid #0f172a; padding-bottom:4px;">
        Sales Invoices Index (${sales.length} Invoices)
      </h3>
      <table class="r-table">
        <thead>
          <tr>
            <th style="width:5%">#</th>
            <th style="width:18%">Invoice #</th>
            <th style="width:28%">Customer</th>
            <th style="width:18%">Date</th>
            <th style="width:13%">Payment</th>
            <th style="width:18%" class="r">Net Amount</th>
          </tr>
        </thead>
        <tbody>
          ${sales.length === 0 ? `
            <tr><td colspan="6" class="c" style="padding:12px;color:#94a3b8">No sales bills in snapshot.</td></tr>
          ` : sales.map((s, idx) => `
            <tr>
              <td class="c">${idx + 1}</td>
              <td style="font-weight:800;color:#0284c7">#${s.invoiceNo || s.vchNo || '-'}</td>
              <td style="font-weight:700">${s.custName || 'Walk-in'}</td>
              <td>${s.date}</td>
              <td><span class="pill-badge ${s.saleType === 'CASH' ? 'pill-cash' : s.saleType === 'ONLINE' ? 'pill-online' : 'pill-credit'}">${s.saleType || 'Cash'}</span></td>
              <td class="r" style="font-weight:900">₹${parseFloat(s.netAmount || s.grossAmount || 0).toFixed(2)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>

    <div class="r-footer-sign">
      <div>Note: Each individual invoice is rendered on the subsequent pages as an authentic pharmacy receipt.</div>
      <div class="sign-area">Authorized Pharmacist</div>
    </div>
  </div>

  <!-- SEPARATE AUTHENTIC RECEIPTS FOR ALL BILLS -->
  ${sales.map((s, idx) => 
    renderSingleBillReceiptMarkup(s, idx + 1, store, `Backup Bill Record ${idx + 1} of ${sales.length}`)
  ).join('')}

  <script>
    window.onload = function() {
      setTimeout(function() { window.print(); }, 350);
    };
  </script>
</body>
</html>`;
};

// Client-side Direct PDF Downloader using html2pdf.js with fallback to native print window
export const downloadReceiptPdf = async (htmlContent, filename = 'Medical_Receipt.pdf') => {
  const cleanFilename = filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;

  // Dynamically ensure html2pdf is loaded
  const ensureHtml2Pdf = () => {
    return new Promise((resolve) => {
      if (window.html2pdf) return resolve(window.html2pdf);
      const existing = document.querySelector('script[src*="html2pdf"]');
      if (existing) {
        if (window.html2pdf) return resolve(window.html2pdf);
        existing.addEventListener('load', () => resolve(window.html2pdf));
        existing.addEventListener('error', () => resolve(null));
        setTimeout(() => resolve(window.html2pdf || null), 2000);
        return;
      }
      const script = document.createElement('script');
      const vendorUrl = `${process.env.PUBLIC_URL || ''}/vendor/html2pdf.bundle.min.js`;
      script.src = vendorUrl;
      script.onload = () => resolve(window.html2pdf);
      script.onerror = () => {
        // Fallback to CDN if local bundle fails
        const fallback = document.createElement('script');
        fallback.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
        fallback.onload = () => resolve(window.html2pdf);
        fallback.onerror = () => resolve(null);
        document.head.appendChild(fallback);
      };
      setTimeout(() => resolve(window.html2pdf || null), 3000);
      document.head.appendChild(script);
    });
  };

  try {
    const html2pdf = await ensureHtml2Pdf();
    if (html2pdf) {
      // Parse HTML to render directly
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      
      // Remove no-print elements and toolbar before generating PDF
      const toolbars = doc.querySelectorAll('.no-print-toolbar');
      toolbars.forEach(t => t.remove());

      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.style.width = '820px';
      container.style.background = '#fff';
      container.innerHTML = doc.body.innerHTML;
      document.body.appendChild(container);

      const opt = {
        margin: [5, 5, 5, 5],
        filename: cleanFilename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      await html2pdf().from(container).set(opt).save();
      document.body.removeChild(container);
      return { success: true, method: 'direct-pdf' };
    }
  } catch (err) {
    console.warn('Direct html2pdf generation failed, falling back to print-to-PDF window:', err);
  }

  // Graceful fallback: Open formatted receipt window which immediately invokes window.print() (native Save as PDF)
  printReceiptDirectly(htmlContent);
  return { success: true, method: 'print-window' };
};

// Directly opens a print window for the receipt HTML
export const printReceiptDirectly = (htmlContent) => {
  const printWindow = window.open('', '_blank', 'width=950,height=850');
  if (!printWindow) {
    alert('Please allow popups to view and print the PDF receipt.');
    return;
  }
  printWindow.document.open();
  printWindow.document.write(htmlContent);
  printWindow.document.close();
};
